import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { Operation } from "src/app/shared/types/entity.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { ImageQuestion } from "../../../../shared/components/dynamic-form/question-image";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { yesNoOptions } from "../../../../shared/constants/components";
import { pattern } from "../../../../shared/constants/patterns";
import { partnerTypes } from "../partner";
import { FILE_RESTRICTIONS, Partner } from "../partner.type";

@Injectable()
export class CreatePartnerService {
  constructor() {}

  generateQuestions(options: {
    partner?: Partner;
    partnerValue?: string;
    disablePartnerId?: boolean;
    showIsResellerNeeded?: boolean;
    operation?: Operation;
    deletePartnerLogoModal?: () => void;
  }): Observable<QuestionBase<string | boolean>[]> {
    const isUpdating = options.operation === Operation.update;
    if (options.disablePartnerId === undefined) {
      options.disablePartnerId = true;
    }
    const questions: QuestionBase<string | boolean>[] = [
      options?.partner &&
        new TextboxQuestion({
          value: options.partner?.partnerIdentifier,
          key: "partnerIdentifier",
          label: "Partner ID",
          required: true,
          order: 1,
          disabled: true,
          copyToClipboard: isUpdating,
        }),

      new TextboxQuestion({
        value: options?.partner?.name,
        key: "name",
        label: "Name",
        required: true,
        order: 2,
        copyToClipboard: isUpdating,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(5),
            message: "Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(200),
            message: "Name is <strong>too long</strong>",
          },
        ],
      }),

      new DropdownQuestion({
        value: options.partner?.type,
        key: "type",
        label: "Type",
        required: true,
        order: 3,
        options: partnerTypes,
        copyToClipboard: isUpdating,
      }),

      new DropdownQuestion({
        value: options.partner?.status,
        key: "status",
        label: "Status",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
        required: true,
        order: 4,
      }),

      new DropdownQuestion({
        value: options?.partner?.isResellerNeeded ?? false,
        key: "isResellerNeeded",
        label: "Reseller Needed",
        options: [...yesNoOptions],
        hide: !options?.showIsResellerNeeded,
        required: false,
        order: 5,
      }),

      new ImageQuestion({
        value: options.partner?.logo?.toString(),
        key: "logo",
        label: "Partner Logo",
        required: false,
        order: 6,
        ...(options.deletePartnerLogoModal && { delete: { show: true, callback: options.deletePartnerLogoModal } }),
        imageReqs: {
          fileRestrictions: {
            maxSize: FILE_RESTRICTIONS.maxSize, // 500 KB
            maxWidth: FILE_RESTRICTIONS.maxWidth,
            maxHeight: FILE_RESTRICTIONS.maxHeight,
          },
          imageName: "",
          fileTooBig: false,
          invalidDimensions: false,
        },
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }
}
